import React, { useState, useCallback, Suspense, Fragment, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import CircularProgress from '@material-ui/core/CircularProgress';

import CookieStorageService from 'services/CookieStorageService';
import ModalService from 'services/ModalService';
import BoostStore from 'stores/BoostStore';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import GamesStore from 'stores/GamesStore';
import PaymentStore from 'stores/PaymentStore';
import ProfileStore from 'stores/ProfileStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import BoostButton from 'components/BoostButton/BoostButton';
import Content from 'components/BoostModal/Content';
import BottomDrawer from 'components/BottomDrawer/BottomDrawer';
import ChatActionButton from 'components/ChatActionButton/ChatActionButton';
import MobilePlayerGiftButton from 'components/MobilePlayerGiftButton/MobilePlayerGiftButton';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import { YEAR_AS_SECONDS } from 'enum/timeFormat';
import { NEW_SHOWS_STATE } from 'enum/videoPlayer';
import allUserGroups from 'enum/userGroups';
import {
  CONTEXTUAL_GOLD_MODAL_AREAS,
  GAMES_STATUS,
} from 'utils/consts';
import { generateID } from 'utils/helpers';
import ThemeService from 'themes/ThemeService';

import goalIcon from '../../../../../../../images/performerVideo/goal-icon-1.png';
import crownIcon from '../../../../../../../images/performerVideo/leader-crown.png';
import styles from '../MobileChatV2.css';


const IDS = {
  prefix: 'S0NyPLsT',
  element: 'ChatActionButton',
  completeGoal: 'completeGoal',
  crown: 'crown',
  boost: 'boost',
  RollTheDice: 'rollTheDice',
  SpinTheWheel: 'spinTheWheel',
  TokenSquares: 'tokenSquares',
  collapse: 'collapse',
};

const makeId = generateID(IDS.prefix, IDS.element);

const DEFAULT_BOOST_PRICE = 25;

const ChatInteractionActions = observer((props) => {
  const {
    user,
    roomName,
    embedProfileV2,
    openDrawer,
    showBuyTokensModal,
    toggleGameDrawer,
    setShowRegistrationModal,
  } = props;
  const { accessHash, username } = user;
  const { boostRemainingTime, newShowsState } = FirebaseStore;
  const { isGoldUser } = UserStore;
  const { toggleInteractionShown, isInteractionShown, openGoalDrawer, closeGoalDrawer } = ChatStore;
  const { gameName, status, pauseTime } = GamesStore;
  const { checkFreeBoosts, freeBoost } = BoostStore;
  const { balance } = PaymentStore;
  const { realityShow } = ProfileStore;

  const boostPrice = window.BOOSTED_ROOM_PRICE_TOKENS || DEFAULT_BOOST_PRICE;
  const shouldRun = pauseTime && status === GAMES_STATUS.PAUSE;
  const isNormalGoalBarVisible =
    TippingStore.isPerformerGoalVisible &&
    newShowsState !== NEW_SHOWS_STATE.GS_SELLING_TICKETS &&
    newShowsState !== NEW_SHOWS_STATE.GS_GOAL_REACHED;

  const [boostOpen, setBoostOpen] = useState(false);
  const [boostContentSettings, setBoostContentSettings] = useState({
    canUpgradeFromBoost: false,
    insufficientBalanceError: false,
    showGoldFeature: false,
    forceOpen: false,
  });

  useEffect(() => {
    if (isGoldUser) {
      checkFreeBoosts({ accessHash, username });
    }
  }, [accessHash, isGoldUser, checkFreeBoosts, username]);

  useEffect(() => {
    if (boostContentSettings.forceOpen) {
      setBoostOpen(true);
    }
  }, [boostContentSettings]);

  const closeBoost = useCallback(() => {
    if (isInteractionShown) {
      toggleInteractionShown();
    }
    setBoostOpen(false);
  }, [isInteractionShown, toggleInteractionShown]);

  const upgradeFromBoostModal = () => {
    closeBoost();

    const isGuest = user.accessLevel <= ACCESS_LEVEL_NUM.GUEST_BILLING;
    if (isGuest) {
      ModalService.showModal(ModalService.MODAL_KEYS.REGISTRATION_GOLD_MODAL, {
        history,
        onClose: ModalService.hideModal,
        showRegistrationGoldModal: true,
      });

      return;
    }

    ModalService.showModal(
      ModalService.MODAL_KEYS.UPGRADE_TO_GOLD_MODAL,
      { area: CONTEXTUAL_GOLD_MODAL_AREAS.BOOST }
    );
  };

  const openBoost = useCallback(() => {
    const notEnoughTokens = balance < boostPrice;
    let updatedBoostContentSettigs = { forceOpen: true };

    CookieStorageService.set('actionButtonId', { buttonId: IDS.boost, broadcaster: roomName },
      { maxAge: YEAR_AS_SECONDS });

    if (!isGoldUser) {
      updatedBoostContentSettigs = {
        ...updatedBoostContentSettigs,
        showGoldFeature: true,
        canUpgradeFromBoost: true,
      };
      if (notEnoughTokens) {
        updatedBoostContentSettigs.insufficientBalanceError = Boolean(notEnoughTokens);
      }
    } else if (notEnoughTokens) {
      if (freeBoost) {
        updatedBoostContentSettigs = {
          ...updatedBoostContentSettigs,
          showGoldFeature: true,
        };
      } else {
        updatedBoostContentSettigs = {
          ...updatedBoostContentSettigs,
          showGoldFeature: false,
          insufficientBalanceError: Boolean(notEnoughTokens),
        };
      }
    } else if (freeBoost) {
      updatedBoostContentSettigs = {
        ...updatedBoostContentSettigs,
        showGoldFeature: true,
      };
    } else {
      updatedBoostContentSettigs = {
        ...updatedBoostContentSettigs,
        showGoldFeature: false,
      };
    }
    setBoostContentSettings({
      ...boostContentSettings,
      ...updatedBoostContentSettigs,
    });
  }, [balance, boostContentSettings, roomName, boostPrice, freeBoost, isGoldUser]);

  const onShowBuyTokensModal = useCallback(() => {
    if (embedProfileV2 && !username) {
      setShowRegistrationModal(true);
    } else {
      showBuyTokensModal();
    }
  }, [embedProfileV2, showBuyTokensModal, setShowRegistrationModal, username]);

  const handleGameIconClick = useCallback(() => {
    const { isGuestUser } = UserStore;


    if (isGuestUser) {
      onShowBuyTokensModal();

      return;
    }
    toggleGameDrawer();
    if (isInteractionShown) {
      toggleInteractionShown();
    }
    CookieStorageService.set('actionButtonId', { buttonId: gameName, broadcaster: roomName },
      { maxAge: YEAR_AS_SECONDS });
  }, [gameName, isInteractionShown, onShowBuyTokensModal, toggleGameDrawer, roomName, toggleInteractionShown]);

  const handleChatAction = useCallback(() => {
    if (isInteractionShown) {
      toggleInteractionShown();
    }
    openDrawer(IDS.crown);
  }, [isInteractionShown, toggleInteractionShown, openDrawer]);

  const handleCompleteGoal = useCallback(() => {
    CookieStorageService.set('actionButtonId', { buttonId: IDS.completeGoal, broadcaster: roomName },
      { maxAge: YEAR_AS_SECONDS });
    openGoalDrawer();
    if (isInteractionShown) {
      toggleInteractionShown();
    }
  }, [openGoalDrawer, isInteractionShown, roomName, toggleInteractionShown]);

  const boostButtonElement = useMemo(() => {
    if (embedProfileV2 || realityShow?.mainEvent) {
      return null;
    }

    return (
      <BoostButton
        icon={ThemeService.theme.boost.icon}
        id={makeId(IDS.boost)}
        shouldRun={!!boostRemainingTime}
        time={boostRemainingTime}
        onClick={openBoost}
      />
    );
  }, [boostRemainingTime, embedProfileV2, openBoost, realityShow]);

  const showSendGiftButton = !embedProfileV2 || UserStore.hasCF(allUserGroups.REALITY_SHOWS);

  return (
    <Fragment>
      <div className={embedProfileV2 ? styles.gamesWrapperEmbed : styles.gamesWrapper}>
        {!embedProfileV2 && isNormalGoalBarVisible && (
          <ChatActionButton
            icon={goalIcon}
            id={makeId(IDS.completeGoal)}
            onClick={handleCompleteGoal}
            onClose={closeGoalDrawer}
          />
        )}

        {boostButtonElement}

        {gameName && (
          <ChatActionButton
            embedProfileV2={embedProfileV2}
            icon={ThemeService.currentGameTheme.icon}
            id={makeId(IDS.gameName)}
            shouldRun={shouldRun}
            time={pauseTime}
            onClick={handleGameIconClick}
          />
        )}
        {showSendGiftButton && (
          <Suspense fallback={<CircularProgress />}>
            <MobilePlayerGiftButton embedProfileV2={embedProfileV2} showBuyTokensModal={showBuyTokensModal} />
          </Suspense>
        )}
        <ChatActionButton
          embedProfileV2={embedProfileV2}
          icon={crownIcon}
          id={makeId(IDS.crown)}
          onClick={handleChatAction}
        />
      </div>
      <BottomDrawer opened={boostOpen} onClose={closeBoost}>
        <Content
          close={closeBoost}
          insufficientBalanceError={boostContentSettings.insufficientBalanceError}
          roomName={roomName}
          showBuyTokensModal={showBuyTokensModal}
          showGoldFeature={boostContentSettings.showGoldFeature}
          upgradeFromBoostModal={boostContentSettings.canUpgradeFromBoost ? upgradeFromBoostModal : null}
          user={user}
        />
      </BottomDrawer>
    </Fragment>
  );
});

export default ChatInteractionActions;
